import React, { useState, useEffect } from 'react';
import { announcementAPI } from '../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { cn } from '../lib/utils';
import { Info, AlertTriangle, AlertOctagon, ChevronRight, Bell } from 'lucide-react';

const PRIORITY_CONFIG = {
    info: { icon: Info, color: 'text-blue-500', bg: 'bg-blue-50 border-blue-200', badge: 'bg-blue-100 text-blue-700', label: 'Info' },
    important: { icon: AlertTriangle, color: 'text-amber-500', bg: 'bg-amber-50 border-amber-200', badge: 'bg-amber-100 text-amber-700', label: 'Important' },
    critical: { icon: AlertOctagon, color: 'text-red-500', bg: 'bg-red-50 border-red-200', badge: 'bg-red-100 text-red-700', label: 'Critical' },
};

export default function AnnouncementModal({ onDismissed }) {
    const [announcements, setAnnouncements] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const fetchUnread = async () => {
            try {
                const res = await announcementAPI.getUnread();
                const items = res.data.announcements || [];
                if (items.length > 0) {
                    // Sort by priority: critical > important > info
                    const order = { critical: 0, important: 1, info: 2 };
                    items.sort((a, b) => (order[a.priority] ?? 2) - (order[b.priority] ?? 2));
                    setAnnouncements(items);
                    setOpen(true);
                }
            } catch {
                // Silently fail - don't block the app
            }
        };
        fetchUnread();
    }, []);

    const current = announcements[currentIndex];

    const handleDismiss = async () => {
        if (current) {
            try {
                await announcementAPI.markRead(current.id);
            } catch { /* ignore */ }
        }

        if (currentIndex < announcements.length - 1) {
            setCurrentIndex(prev => prev + 1);
        } else {
            setOpen(false);
            onDismissed?.();
        }
    };

    const handleDismissAll = async () => {
        // Mark all remaining as read
        for (let i = currentIndex; i < announcements.length; i++) {
            try {
                await announcementAPI.markRead(announcements[i].id);
            } catch { /* ignore */ }
        }
        setOpen(false);
        onDismissed?.();
    };

    if (!open || !current) return null;

    const config = PRIORITY_CONFIG[current.priority] || PRIORITY_CONFIG.info;
    const Icon = config.icon;
    const remaining = announcements.length - currentIndex;

    return (
        <Dialog open={open} onOpenChange={() => {}}>
            <DialogContent
                className="sm:max-w-lg"
                data-testid="announcement-modal"
                onPointerDownOutside={(e) => e.preventDefault()}
                onEscapeKeyDown={(e) => e.preventDefault()}
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2" data-testid="announcement-modal-title">
                        <Bell className="h-5 w-5 text-indigo-500" />
                        Announcement
                        {remaining > 1 && (
                            <span className="text-xs font-normal text-slate-400 ml-auto">
                                {currentIndex + 1} of {announcements.length}
                            </span>
                        )}
                    </DialogTitle>
                </DialogHeader>

                <div className={cn("rounded-lg border p-4 space-y-3", config.bg)} data-testid="announcement-content">
                    <div className="flex items-start gap-3">
                        <Icon className={cn("h-5 w-5 mt-0.5 shrink-0", config.color)} />
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                                <h3 className="font-semibold text-slate-900 text-base">{current.title}</h3>
                                <Badge className={cn("text-xs", config.badge)}>{config.label}</Badge>
                            </div>
                            <div className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed" data-testid="announcement-body">
                                {current.body}
                            </div>
                        </div>
                    </div>
                    <p className="text-xs text-slate-400 text-right">
                        {new Date(current.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </p>
                </div>

                <DialogFooter className="flex gap-2 sm:justify-between">
                    {remaining > 1 && (
                        <Button variant="ghost" size="sm" className="text-slate-400 hover:text-slate-600" onClick={handleDismissAll} data-testid="announcement-dismiss-all">
                            Dismiss all ({remaining})
                        </Button>
                    )}
                    <Button onClick={handleDismiss} className="bg-indigo-600 hover:bg-indigo-700 text-white ml-auto" data-testid="announcement-acknowledge-btn">
                        {remaining > 1 ? (
                            <>Next <ChevronRight className="h-4 w-4 ml-1" /></>
                        ) : (
                            'Got it'
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
